<template>
    <div class="page quiz-set-answer-page">
        <div class="page-header">
            <div class="page-header-content">
                <h1>
                    {{
                        quizInfo?.scoring_mode === 'dimensions'
                            ? '設定維度加權'
                            : '設定答案'
                    }}{{ quizName ? ` - ${quizName}` : '' }}
                </h1>
                <a-button
                    type="text"
                    class="header-back-button"
                    @click="goBack"
                >
                    返回
                </a-button>
            </div>
        </div>

        <div
            v-if="!loading && questions && questions.length > 0"
            class="content-container"
        >
            <a-row :gutter="24">
                <!-- 左側：測驗基本資訊 -->
                <a-col :span="7">
                    <div class="info-card quiz-info-card">
                        <div class="card-title-wrapper">
                            <h3 class="card-title">
                                {{ quizInfo?.name || quizName || '測驗資訊' }}
                            </h3>
                            <a-tag
                                v-if="quizInfo?.scoring_mode_display"
                                class="scoring-mode-tag"
                                :color="
                                    getScoringModeColor(quizInfo.scoring_mode)
                                "
                            >
                                {{ quizInfo.scoring_mode_display }}
                            </a-tag>
                        </div>
                        <div class="info-content">
                            <div class="info-item">
                                <div class="info-label">說明</div>
                                <div class="info-value description-value">
                                    {{ quizInfo?.description || '-' }}
                                </div>
                            </div>

                            <div class="info-item">
                                <div class="info-label">狀態</div>
                                <div class="info-value">
                                    {{ quizInfo?.status_display || '-' }}
                                </div>
                            </div>

                            <div
                                v-if="
                                    quizInfo?.scoring_mode !== 'survey' &&
                                    quizInfo?.scoring_mode !== 'dimensions'
                                "
                                class="info-item"
                            >
                                <div class="info-label">總分</div>
                                <div class="info-value">
                                    {{ quizInfo?.total_score || '-' }}
                                </div>
                            </div>

                            <div
                                v-if="quizInfo?.scoring_mode === 'normal'"
                                class="info-item"
                            >
                                <div class="info-label">及格分數</div>
                                <div class="info-value">
                                    {{ quizInfo?.passing_score || '-' }}
                                </div>
                            </div>

                            <div class="info-item">
                                <div class="info-label">時間限制</div>
                                <div class="info-value">
                                    {{ quizInfo?.time_limit_display || '-' }}
                                </div>
                            </div>

                            <div class="info-item">
                                <div class="info-label">最大作答次數</div>
                                <div class="info-value">
                                    {{
                                        quizInfo?.max_attempts_per_user ||
                                        quizInfo?.max_attempts_per_user === 0
                                            ? quizInfo.max_attempts_per_user ===
                                              0
                                                ? '無限制'
                                                : quizInfo.max_attempts_per_user
                                            : '-'
                                    }}
                                </div>
                            </div>

                            <div class="info-item">
                                <div class="info-label">總作答次數</div>
                                <div class="info-value">
                                    {{ quizInfo?.total_attempts || 0 }}
                                </div>
                            </div>

                            <div class="info-item">
                                <div class="info-label">已完成次數</div>
                                <div class="info-value">
                                    {{ quizInfo?.completed_attempts || 0 }}
                                </div>
                            </div>

                            <div class="info-item">
                                <div class="info-label">建立者</div>
                                <div class="info-value">
                                    {{ quizInfo?.created_by || '-' }}
                                </div>
                            </div>

                            <div class="info-item">
                                <div class="info-label">建立時間</div>
                                <div class="info-value">
                                    {{ quizInfo?.created_at || '-' }}
                                </div>
                            </div>

                            <div class="info-item">
                                <div class="info-label">更新者</div>
                                <div class="info-value">
                                    {{ quizInfo?.updated_by || '-' }}
                                </div>
                            </div>

                            <div class="info-item">
                                <div class="info-label">更新時間</div>
                                <div class="info-value">
                                    {{ quizInfo?.updated_at || '-' }}
                                </div>
                            </div>
                        </div>
                    </div>
                </a-col>

                <!-- 右側：題目列表 -->
                <a-col :span="17">
                    <div class="questions-list">
                        <div
                            v-for="(q, idx) in questions"
                            :key="q.id"
                            class="question-card"
                        >
                            <!-- 題目標題 -->
                            <div class="question-header">
                                <div class="question-content-row">
                                    <div class="question-left-section">
                                        <div
                                            class="question-content-cell question-title-content"
                                        >
                                            題目：{{ q?.title || '題目標題' }}
                                        </div>
                                        <div
                                            v-if="q?.description"
                                            class="question-content-cell question-description-content"
                                        >
                                            說明：{{ q.description }}
                                        </div>
                                    </div>
                                    <div
                                        class="question-content-cell question-type-content"
                                    >
                                        <a-tag
                                            :class="[
                                                'question-type-tag',
                                                `question-type-tag-${q?.question_type || 'default'}`
                                            ]"
                                        >
                                            {{
                                                q?.question_type_display || '-'
                                            }}
                                            <span
                                                v-if="q?.is_required"
                                                class="required-star"
                                            >
                                                *
                                            </span>
                                        </a-tag>
                                        <!-- 人工審核模式：題目層級分數輸入 -->
                                        <div
                                            v-if="q?.scoring_mode === 'manual'"
                                            class="question-score-wrapper"
                                        >
                                            <span class="question-score-label">
                                                分數
                                            </span>
                                            <a-input-number
                                                v-model:value="q.question_score"
                                                :min="0"
                                                :precision="0"
                                                placeholder="0"
                                                class="question-score-input"
                                                size="small"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- 單選/多選/量表題：選項 -->
                            <div
                                v-if="
                                    q?.question_type === 'single' ||
                                    q?.question_type === 'multiple' ||
                                    q?.question_type === 'scale'
                                "
                                class="options-container"
                            >
                                <div
                                    v-for="(option, optIdx) in q.options"
                                    :key="option.id"
                                    class="option-row"
                                    :class="{
                                        'option-selected': isOptionSelected(
                                            q,
                                            option
                                        ),
                                        'option-row-clickable':
                                            (q?.question_type === 'single' ||
                                                q?.question_type ===
                                                    'multiple' ||
                                                q?.question_type === 'scale') &&
                                            q?.scoring_mode !== 'manual' &&
                                            quizInfo?.scoring_mode !==
                                                'dimensions'
                                    }"
                                    @click="
                                        ((q?.question_type === 'single' ||
                                            q?.question_type === 'scale') &&
                                            setSingleAnswer(idx, option.id)) ||
                                        (q?.question_type === 'multiple' &&
                                            toggleMultipleAnswer(idx, option))
                                    "
                                >
                                    <div class="option-left">
                                        <label
                                            v-if="
                                                (q?.question_type ===
                                                    'single' ||
                                                    q?.question_type ===
                                                        'scale') &&
                                                quizInfo?.scoring_mode !==
                                                    'dimensions'
                                            "
                                            class="option-radio-wrapper"
                                            :class="{
                                                'option-disabled':
                                                    q?.scoring_mode === 'manual'
                                            }"
                                        >
                                            <input
                                                type="radio"
                                                :value="String(option.id)"
                                                :name="`answer-q-${q.id}`"
                                                :checked="
                                                    isOptionSelected(q, option)
                                                "
                                                class="option-radio-input"
                                                :disabled="
                                                    q?.scoring_mode === 'manual'
                                                "
                                                @change="
                                                    (q?.question_type ===
                                                        'single' ||
                                                        q?.question_type ===
                                                            'scale') &&
                                                    setSingleAnswer(
                                                        idx,
                                                        option.id
                                                    )
                                                "
                                            />
                                            <span
                                                class="option-radio-mark"
                                            ></span>
                                        </label>

                                        <label
                                            v-if="
                                                q?.question_type ===
                                                    'multiple' &&
                                                quizInfo?.scoring_mode !==
                                                    'dimensions'
                                            "
                                            class="option-checkbox-wrapper"
                                            :class="{
                                                'option-disabled':
                                                    q?.scoring_mode === 'manual'
                                            }"
                                        >
                                            <input
                                                type="checkbox"
                                                :checked="
                                                    isOptionSelected(q, option)
                                                "
                                                class="option-checkbox-input"
                                                :disabled="
                                                    q?.scoring_mode === 'manual'
                                                "
                                                @change="
                                                    toggleMultipleAnswer(
                                                        idx,
                                                        option
                                                    )
                                                "
                                            />
                                            <span
                                                class="option-checkbox-mark"
                                            ></span>
                                        </label>
                                        <span class="option-text">
                                            {{ option?.label || '' }}
                                        </span>
                                    </div>
                                    <!-- 自動計分模式才顯示選項分數（點擊不冒泡，避免觸發整列切換選中） -->
                                    <div
                                        v-if="
                                            isOptionSelected(q, option) &&
                                            q?.scoring_mode !== 'manual' &&
                                            quizInfo?.scoring_mode !==
                                                'dimensions'
                                        "
                                        class="option-score-wrapper"
                                        @click.stop
                                    >
                                        <span class="score-label-inline">
                                            分數
                                        </span>
                                        <a-input-number
                                            v-model:value="option.score"
                                            :min="0"
                                            :precision="0"
                                            placeholder="0"
                                            class="score-input-inline"
                                            size="small"
                                        />
                                    </div>

                                    <!-- 維度類型：顯示維度加權輸入（每個選項下方） -->
                                    <div
                                        v-if="
                                            quizInfo?.scoring_mode ===
                                                'dimensions' &&
                                            quizInfo?.dimensions &&
                                            quizInfo.dimensions.length > 0
                                        "
                                        class="option-dimension-weights"
                                    >
                                        <div
                                            v-for="dimension in quizInfo.dimensions"
                                            :key="dimension.id"
                                            class="dimension-weight-item"
                                        >
                                            <span
                                                class="dimension-weight-label"
                                            >
                                                {{
                                                    dimension.name ||
                                                    `維度${dimension.sequence || ''}`
                                                }}
                                            </span>
                                            <a-input-number
                                                :value="
                                                    getDimensionWeight(
                                                        option,
                                                        dimension.id
                                                    )
                                                "
                                                @change="
                                                    (val) =>
                                                        setDimensionWeight(
                                                            option,
                                                            dimension.id,
                                                            val
                                                        )
                                                "
                                                :min="0"
                                                :precision="0"
                                                :step="1"
                                                placeholder="0"
                                                class="dimension-weight-input"
                                                size="small"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- 文字/數字/長文字題：答案輸入（需有 correct_answers 才綁定，避免未正規化時報錯） -->
                            <div
                                v-else-if="
                                    q?.question_type === 'text' ||
                                    q?.question_type === 'number' ||
                                    q?.question_type === 'long_text'
                                "
                                class="text-answer-container"
                            >
                                <div
                                    v-if="q.correct_answers"
                                    class="text-answer-row"
                                >
                                    <div class="text-answer-input-wrapper">
                                        <div class="text-answer-label-inline">
                                            正確答案
                                        </div>
                                        <a-textarea
                                            v-if="
                                                q?.question_type === 'text' ||
                                                q?.question_type === 'long_text'
                                            "
                                            v-model:value="
                                                q.correct_answers.answer
                                            "
                                            placeholder="請輸入正確答案"
                                            :rows="
                                                q?.question_type === 'long_text'
                                                    ? 5
                                                    : 3
                                            "
                                            class="text-answer-textarea"
                                            :disabled="
                                                q?.scoring_mode === 'manual'
                                            "
                                        />
                                        <a-input-number
                                            v-else-if="
                                                q?.question_type === 'number'
                                            "
                                            v-model:value="
                                                q.correct_answers.answer
                                            "
                                            placeholder="請輸入正確答案"
                                            class="text-answer-input"
                                            :disabled="
                                                q?.scoring_mode === 'manual'
                                            "
                                        />
                                    </div>
                                    <!-- 自動計分模式才顯示答案層級分數 -->
                                    <div
                                        v-if="q?.scoring_mode !== 'manual'"
                                        class="text-answer-score-wrapper"
                                    >
                                        <div class="text-answer-label-inline">
                                            分數
                                        </div>
                                        <a-input-number
                                            v-model:value="
                                                q.correct_answers.score
                                            "
                                            :min="0"
                                            :precision="0"
                                            placeholder="0"
                                            class="score-input-inline"
                                            size="small"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- 儲存按鈕 -->
                    <div class="action-bar">
                        <a-button
                            type="primary"
                            class="save-button"
                            @click="saveSetting"
                            size="large"
                            :loading="loading"
                        >
                            <span>儲存答案</span>
                        </a-button>
                    </div>
                </a-col>
            </a-row>
        </div>

        <div
            v-else-if="!loading && (!questions || questions.length === 0)"
            class="empty-state"
        >
            <div class="empty-icon">📝</div>
            <div class="empty-text">此測驗尚無題目</div>
        </div>
    </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed, watch, h } from 'vue';
import { message, Modal } from 'ant-design-vue';
import { showErrorMessage } from '@/utils/messageHelper';
import { ReloadOutlined } from '@ant-design/icons-vue';
import {
    getQuestions,
    setAnswers,
    setBatchAnswers,
    getSingleQuiz,
    updateQuizzes,
    setBatchOptionWeights
} from '@/api/quiz';
import { useRouter, useRoute } from 'vue-router';

const router = useRouter();
const route = useRoute();
const { params } = route;
const { id } = params;
const loading = ref(false);

let questions = ref(null);
const quizName = ref('');
const quizInfo = ref(null);

// 獲取測驗資訊以顯示測驗名稱
const loadQuizInfo = async () => {
    try {
        // 先載入基本測驗資訊以判斷模式
        let response = await getSingleQuiz(id);
        if (response.result && response.data) {
            quizInfo.value = response.data;
            quizName.value = response.data.name || '';

            // 如果是維度模式，再次載入帶 include=answers 以取得維度資料
            if (response.data.scoring_mode === 'dimensions') {
                response = await getSingleQuiz(id, 'answers');
                if (response.result && response.data) {
                    quizInfo.value = response.data;
                }
            }
        }
    } catch (error) {
        console.error('載入測驗資訊失敗:', error);
    }
};

// 判斷選項是否被選中（單選/量表存 option id，多選存選項物件陣列；id 可能為字串或數字）
const isOptionSelected = (question, option) => {
    if (
        question?.question_type === 'single' ||
        question?.question_type === 'scale'
    ) {
        if (question.correct_answers == null) return false;
        return Number(question.correct_answers) === Number(option.id);
    } else if (question?.question_type === 'multiple') {
        return (
            Array.isArray(question.correct_answers) &&
            question.correct_answers.some(
                (ans) => Number(ans?.id) === Number(option.id)
            )
        );
    }
    return false;
};

// 單選/量表：點擊整列時直接寫入 questions.value[questionIdx]，確保 reactivity 與送出時讀到一致
const setSingleAnswer = (questionIdx, optionId) => {
    if (
        !questions.value ||
        questionIdx < 0 ||
        questionIdx >= questions.value.length
    )
        return;
    const q = questions.value[questionIdx];
    if (
        (q?.question_type !== 'single' && q?.question_type !== 'scale') ||
        q?.scoring_mode === 'manual'
    )
        return;
    q.correct_answers = String(optionId);
};

// 多選：切換選項是否在正確答案陣列中（直接改 questions.value[questionIdx]）
const toggleMultipleAnswer = (questionIdx, option) => {
    if (
        !questions.value ||
        questionIdx < 0 ||
        questionIdx >= questions.value.length
    )
        return;
    const q = questions.value[questionIdx];
    if (q?.question_type !== 'multiple' || q?.scoring_mode === 'manual') return;
    if (!Array.isArray(q.correct_answers)) q.correct_answers = [];
    const idx = q.correct_answers.findIndex(
        (ans) => Number(ans?.id) === Number(option.id)
    );
    if (idx >= 0) {
        q.correct_answers.splice(idx, 1);
    } else {
        q.correct_answers.push(option);
    }
};

// 獲取計分模式顏色（Morandi 色系）
const getScoringModeColor = (scoringMode) => {
    // Morandi 色系：莫蘭迪藍、莫蘭迪黃、莫蘭迪綠
    // 同時支援英文和中文格式
    const colorMap = {
        normal: '#3b6ea5', // Morandi 藍（測驗）
        '測驗': '#3b6ea5',
        dimensions: '#B8A05A', // Morandi 黃（多維）
        '多維': '#B8A05A',
        '維度': '#B8A05A',
        survey: '#9BB7D4', // Morandi 綠（問卷）
        '問卷': '#9BB7D4'
    };
    return colorMap[scoringMode] || 'default';
};

// 返回按鈕處理
const goBack = () => {
    router.push({
        name: 'QuizzesManagement'
    });
};

// 獲取維度加權值（確保類型一致性，返回整數）
const getDimensionWeight = (option, dimensionId) => {
    if (!option || !option.weights) {
        return 0;
    }

    // 嘗試多種鍵值類型匹配
    const idNum = Number(dimensionId);
    const idStr = String(dimensionId);
    let weight = 0;

    // 優先使用數字鍵
    if (
        !isNaN(idNum) &&
        option.weights[idNum] !== undefined &&
        option.weights[idNum] !== null
    ) {
        weight = Number(option.weights[idNum]) || 0;
    }
    // 嘗試字串鍵
    else if (
        option.weights[idStr] !== undefined &&
        option.weights[idStr] !== null
    ) {
        weight = Number(option.weights[idStr]) || 0;
    }
    // 嘗試直接匹配
    else if (
        option.weights[dimensionId] !== undefined &&
        option.weights[dimensionId] !== null
    ) {
        weight = Number(option.weights[dimensionId]) || 0;
    }

    // 返回整數（四捨五入）
    return Math.round(weight);
};

// 設置維度加權值（確保為整數）
const setDimensionWeight = (option, dimensionId, value) => {
    if (!option) {
        return;
    }

    if (!option.weights) {
        option.weights = {};
    }

    const numVal = value !== null && value !== undefined ? Number(value) : 0;
    // 轉換為整數（四捨五入）
    const finalVal = isNaN(numVal) ? 0 : Math.round(numVal);

    // 同時設置數字和字串鍵，確保兼容性
    const idNum = Number(dimensionId);
    if (!isNaN(idNum)) {
        option.weights[idNum] = finalVal;
    }
    option.weights[dimensionId] = finalVal;
};

// 載入測驗內容
const loadQuestions = async () => {
    try {
        loading.value = true;

        // 如果是維度類型，使用 getSingleQuiz 並包含 answers 以取得維度加權數據
        let response;
        let questionsData = [];

        // 如果 quizInfo 還沒載入，先載入一次以判斷類型
        if (!quizInfo.value) {
            const infoResponse = await getSingleQuiz(id);
            if (infoResponse.result && infoResponse.data) {
                quizInfo.value = infoResponse.data;
                quizName.value = infoResponse.data.name || '';
            }
        }

        if (quizInfo.value?.scoring_mode === 'dimensions') {
            response = await getSingleQuiz(id, 'answers');
            // 更新 quizInfo 以確保有最新的維度資料
            if (response.result && response.data) {
                quizInfo.value = response.data;
                // 從 response.data.questions 取得題目列表
                questionsData = response.data.questions || [];
            }
        } else {
            // 非維度類型：使用 include=answers 來判斷是否已設置答案
            response = await getQuestions(id, { include: 'answers' });
            if (response.result) {
                questionsData = response.data.data || [];
            }
        }

        if (response.result && questionsData.length > 0) {
            questionsData.forEach((q) => {
                // 人工審核模式：初始化題目層級分數
                if (q?.scoring_mode === 'manual') {
                    // 檢查是否有已設置的題目層級分數
                    let existingScore = 0;
                    if (q.score !== null && q.score !== undefined) {
                        existingScore = Number(q.score) || 0;
                    } else if (q.max_score) {
                        existingScore = Number(q.max_score) || 0;
                    }
                    q.question_score = existingScore;
                }

                // 選項題型（single, multiple, scale）：使用 correct_answers（複數，陣列）
                if (
                    q?.question_type === 'single' ||
                    q?.question_type === 'multiple' ||
                    q?.question_type === 'scale'
                ) {
                    // correct_answers 格式：[{ "option_id": 19, "score": 10.0 }]
                    if (
                        q.correct_answers &&
                        Array.isArray(q.correct_answers) &&
                        q.correct_answers.length > 0
                    ) {
                        // 匹配選項並更新分數
                        const matchedOptions = [];

                        q.correct_answers.forEach((correctAns) => {
                            // 找到對應的選項（option_id 與 opt.id 可能為字串或數字，用寬鬆比對）
                            const matchedOption = q.options.find(
                                (opt) =>
                                    Number(opt.id) ===
                                    Number(correctAns.option_id)
                            );
                            if (matchedOption) {
                                // 更新選項的分數（從 correct_answers 中取得）
                                matchedOption.score = correctAns.score || 0;
                                // 初始化維度加權數據
                                if (!matchedOption.weights) {
                                    matchedOption.weights = {};
                                }
                                matchedOptions.push(matchedOption);
                            }
                        });

                        // 設定正確答案：單選/量表存選項 id（字串，與 radio value 一致），多選存選項物件陣列
                        if (
                            q?.question_type === 'single' ||
                            q?.question_type === 'scale'
                        ) {
                            q.correct_answers =
                                matchedOptions.length > 0
                                    ? String(matchedOptions[0].id)
                                    : null;
                        } else {
                            // 多選題：使用所有匹配的選項陣列
                            q.correct_answers = matchedOptions;
                        }
                    } else {
                        // 沒有正確答案（correct_answers 為 null 或空陣列），初始化
                        if (
                            q?.question_type === 'single' ||
                            q?.question_type === 'scale'
                        ) {
                            q.correct_answers = null;
                        } else {
                            q.correct_answers = [];
                        }
                    }

                    // 處理維度加權數據：從選項的 weights 陣列載入（維度類型）
                    if (q.options && Array.isArray(q.options)) {
                        q.options.forEach((option) => {
                            // 保存原始的 weights 數據（可能是陣列）
                            const originalWeights = option.weights;

                            // 如果選項有 weights 陣列（從 API 載入），轉換為物件格式
                            if (
                                originalWeights &&
                                Array.isArray(originalWeights)
                            ) {
                                const weightsObj = {};
                                originalWeights.forEach((weightItem) => {
                                    if (
                                        weightItem.dimension_id !== undefined &&
                                        weightItem.dimension_id !== null &&
                                        weightItem.weight !== undefined &&
                                        weightItem.weight !== null &&
                                        weightItem.weight !== ''
                                    ) {
                                        // 確保 dimension_id 和 weight 都轉換為數字
                                        // dimension_id 可能是數字或字串，都要轉換為數字
                                        // 注意：API 返回的 dimension_id 和 quizInfo.dimensions 中的 id 類型需要一致
                                        const dimId =
                                            typeof weightItem.dimension_id ===
                                            'number'
                                                ? weightItem.dimension_id
                                                : Number(
                                                      weightItem.dimension_id
                                                  );
                                        // 將 weight 轉換為整數
                                        const weightNum =
                                            typeof weightItem.weight ===
                                            'number'
                                                ? weightItem.weight
                                                : Number(weightItem.weight);
                                        const weight = !isNaN(weightNum)
                                            ? Math.round(weightNum)
                                            : 0;

                                        // 檢查轉換是否成功
                                        if (
                                            !isNaN(dimId) &&
                                            weight !== null &&
                                            weight !== undefined
                                        ) {
                                            weightsObj[dimId] = weight;
                                        } else {
                                            // 調試用：記錄轉換失敗的情況
                                            console.warn(
                                                '維度加權數據轉換失敗:',
                                                {
                                                    dimension_id:
                                                        weightItem.dimension_id,
                                                    weight: weightItem.weight,
                                                    dimId,
                                                    weightNum: weight
                                                }
                                            );
                                        }
                                    }
                                });
                                // 確保 option.weights 被正確賦值
                                option.weights = weightsObj;
                            } else if (
                                !originalWeights ||
                                typeof originalWeights !== 'object' ||
                                Array.isArray(originalWeights)
                            ) {
                                // 如果沒有 weights 或不是物件格式，初始化為空物件
                                option.weights = {};
                            }
                        });
                    }
                }
                // 非選項題型（text, long_text, number）：使用 correct_answer（單數，字串）
                else if (
                    q?.question_type === 'text' ||
                    q?.question_type === 'long_text' ||
                    q?.question_type === 'number'
                ) {
                    // correct_answer 是字串（或 null）
                    // 檢查是否有已設置的分數
                    // 根據 API 回應，文字題的分數可能需要從其他地方取得
                    // 如果 answers 陣列不為空，嘗試從中取得分數
                    let existingScore = 0;
                    if (
                        q.answers &&
                        Array.isArray(q.answers) &&
                        q.answers.length > 0
                    ) {
                        // 從 answers 中取得分數
                        existingScore =
                            Number(q.answers[0]?.score) ||
                            Number(q.answers[0]?.point) ||
                            0;
                    }

                    // 如果 answers 中沒有分數，檢查是否有 score 欄位（可能在題目層級）
                    if (
                        existingScore === 0 &&
                        q.score !== null &&
                        q.score !== undefined
                    ) {
                        existingScore = Number(q.score) || 0;
                    }

                    // 如果還是 0，使用 max_score 作為預設值（讓用戶看到最大分數）
                    if (existingScore === 0 && q.max_score) {
                        existingScore = Number(q.max_score) || 0;
                    }

                    if (
                        q.correct_answer !== null &&
                        q.correct_answer !== undefined
                    ) {
                        // 將 correct_answer（字串）轉換為 { answer, score } 物件格式
                        q.correct_answers = {
                            answer: String(q.correct_answer),
                            score: existingScore
                        };
                    } else {
                        // 沒有答案，初始化為空物件
                        q.correct_answers = {
                            answer: '',
                            score: existingScore
                        };
                    }
                } else {
                    // 其他類型，初始化
                    q.correct_answers = null;
                }
            });

            // 使用 reactive 包裝，讓巢狀屬性（如 q.correct_answers）變更能觸發視圖更新
            questions.value = reactive(questionsData);
        } else if (
            response.result &&
            (!questionsData || questionsData.length === 0)
        ) {
            message.warning(
                '尚未設置題目，請先編輯測驗新增題目後再進行答案設置。'
            );
            goBack();
        } else {
            showErrorMessage(response.message || '載入失敗');
        }
    } catch (error) {
        showErrorMessage('載入題目列表失敗');
    } finally {
        loading.value = false;
    }
};

const saveSetting = async (e) => {
    // 維度類型：只檢查維度加權是否設置
    if (quizInfo.value?.scoring_mode === 'dimensions') {
        const unweightedQuestions = [];

        questions.value.forEach((q, idx) => {
            // 只檢查單選/多選題的選項維度加權
            if (
                (q?.question_type === 'single' ||
                    q?.question_type === 'multiple') &&
                q.options &&
                Array.isArray(q.options)
            ) {
                let hasWeight = false;

                // 檢查是否有任何選項設置了維度加權
                q.options.forEach((option) => {
                    if (
                        option.weights &&
                        Object.keys(option.weights).length > 0
                    ) {
                        // 檢查是否有至少一個維度的加權值不是空值
                        quizInfo.value.dimensions.forEach((dimension) => {
                            const weight = option.weights[dimension.id];
                            if (
                                weight !== undefined &&
                                weight !== null &&
                                weight !== ''
                            ) {
                                hasWeight = true;
                            }
                        });
                    }
                });

                if (!hasWeight) {
                    unweightedQuestions.push(idx + 1);
                }
            }
        });

        if (unweightedQuestions.length > 0) {
            showErrorMessage(
                `以下題目尚未設置維度加權：第 ${unweightedQuestions.join('、')} 題`
            );
            return;
        }

        // 維度類型只需要保存維度加權，不需要保存答案
        // 直接進行保存維度加權的邏輯
        loading.value = true;

        try {
            const optionWeights = [];

            questions.value.forEach((q) => {
                if (
                    (q?.question_type === 'single' ||
                        q?.question_type === 'multiple') &&
                    q.options &&
                    Array.isArray(q.options)
                ) {
                    q.options.forEach((option) => {
                        if (
                            option.weights &&
                            Object.keys(option.weights).length > 0
                        ) {
                            quizInfo.value.dimensions.forEach((dimension) => {
                                const weight = option.weights[dimension.id];
                                if (
                                    weight !== undefined &&
                                    weight !== null &&
                                    weight !== ''
                                ) {
                                    optionWeights.push({
                                        question_id: q.id,
                                        option_id: option.id,
                                        dimension_id: dimension.id,
                                        weight: Number(weight) || 0
                                    });
                                }
                            });
                        }
                    });
                }
            });

            if (optionWeights.length > 0) {
                await setBatchOptionWeights(id, {
                    option_weights: optionWeights
                });
                message.success('設定維度加權成功');
            } else {
                message.warning('沒有需要保存的維度加權數據');
            }

            // 檢查測驗狀態，如果還沒發布，詢問是否要發布
            try {
                const quizResponse = await getSingleQuiz(id);
                if (quizResponse.result && quizResponse.data) {
                    const quizStatus = quizResponse.data.status;
                    const quizData = quizResponse.data;

                    // 如果還沒發布，顯示確認對話框
                    if (quizStatus !== 'published') {
                        const questionCount = questions.value
                            ? questions.value.length
                            : 0;

                        Modal.confirm({
                            title: '維度加權設置完成',
                            content: `已為 ${questionCount} 題設置維度加權，是否要發布測驗？`,
                            okText: '發布',
                            cancelText: '稍後',
                            onOk: async () => {
                                try {
                                    await updateQuizzes(id, {
                                        status: 'published'
                                    });
                                    message.success('測驗已發布');
                                } catch (error) {
                                    showErrorMessage('發布失敗');
                                }
                            },
                            onCancel: () => {
                                message.info('已保存維度加權設置');
                            }
                        });
                    } else {
                        message.success('維度加權設置已更新');
                    }
                }
            } catch (error) {
                console.error('檢查測驗狀態失敗:', error);
            }
        } catch (error) {
            const { response } = error;
            const { data: d } = response || {};
            const { message: m } = d || {};
            showErrorMessage(m || '設定維度加權失敗');
        } finally {
            loading.value = false;
        }

        return; // 維度類型直接返回，不執行後續的答案保存邏輯
    }

    // 非維度類型：檢查所有題目是否都已設置答案（人工審核模式不需要檢查答案）
    const unansweredQuestions = [];

    questions.value.forEach((q, idx) => {
        // 人工審核模式不需要檢查答案，只需要檢查分數
        if (q?.scoring_mode === 'manual') {
            // 人工審核模式只需要有分數即可
            if (!q.question_score || q.question_score <= 0) {
                unansweredQuestions.push(idx + 1);
            }
            return;
        }

        let hasAnswer = false;

        if (q?.question_type === 'single') {
            // 單選題：必須選擇一個選項
            hasAnswer =
                q.correct_answers !== null && q.correct_answers !== undefined;
        } else if (q?.question_type === 'multiple') {
            // 多選題：必須至少選擇一個選項
            hasAnswer =
                Array.isArray(q.correct_answers) &&
                q.correct_answers.length > 0;
        } else if (
            q?.question_type === 'text' ||
            q?.question_type === 'number' ||
            q?.question_type === 'long_text'
        ) {
            // 文字/數字/長文字題：必須輸入答案
            hasAnswer =
                q.correct_answers &&
                q.correct_answers.answer &&
                String(q.correct_answers.answer).trim() !== '';
        }

        if (!hasAnswer) {
            unansweredQuestions.push(idx + 1); // 題目編號從1開始
        }
    });

    if (unansweredQuestions.length > 0) {
        showErrorMessage(
            `以下題目尚未設置答案或分數：第 ${unansweredQuestions.join('、')} 題`
        );
        return;
    }

    let totalAnswers = [];

    questions.value.forEach((q) => {
        // 人工審核模式：分數在題目層級
        if (q?.scoring_mode === 'manual') {
            totalAnswers.push({
                question_id: q.id,
                score: +q.question_score || 0
            });
            return;
        }

        if (q?.question_type === 'single' || q?.question_type === 'scale') {
            if (q.correct_answers != null && q.options?.length) {
                const opt = q.options.find(
                    (o) => Number(o.id) === Number(q.correct_answers)
                );
                if (opt) {
                    totalAnswers.push({
                        question_id: q.id,
                        correct_answers: [
                            {
                                option_id: opt.id,
                                score: +opt.score || 0
                            }
                        ]
                    });
                }
            }
        }

        if (q?.question_type === 'multiple') {
            let answers = [];

            if (
                Array.isArray(q.correct_answers) &&
                q.correct_answers.length > 0
            ) {
                q.correct_answers.forEach((a) => {
                    answers.push({
                        option_id: a.id,
                        score: +a.score || 0
                    });
                });
            }

            totalAnswers.push({
                question_id: q.id,
                correct_answers: answers
            });
        }

        if (
            q?.question_type === 'text' ||
            q?.question_type === 'number' ||
            q?.question_type === 'long_text'
        ) {
            if (q.correct_answers && q.correct_answers.answer) {
                totalAnswers.push({
                    question_id: q.id,
                    correct_answer: String(q.correct_answers.answer).trim(),
                    score: +q.correct_answers.score || 0
                });
            }
        }
    });

    loading.value = true;

    // 非維度類型：保存答案
    setBatchAnswers(id, { 'questions_answers': totalAnswers })
        .then(async (res) => {
            message.success('設定答案成功');

            // 計算總分
            let totalScore = 0;
            questions.value.forEach((q) => {
                if (
                    q?.question_type === 'single' ||
                    q?.question_type === 'scale'
                ) {
                    const singleOpt = q.options?.find(
                        (o) => Number(o.id) === Number(q.correct_answers)
                    );
                    if (singleOpt) {
                        totalScore += Number(singleOpt.score) || 0;
                    }
                } else if (q?.question_type === 'multiple') {
                    if (
                        Array.isArray(q.correct_answers) &&
                        q.correct_answers.length > 0
                    ) {
                        q.correct_answers.forEach((ans) => {
                            totalScore += Number(ans.score) || 0;
                        });
                    }
                } else if (
                    q?.question_type === 'text' ||
                    q?.question_type === 'long_text' ||
                    q?.question_type === 'number'
                ) {
                    if (q.correct_answers && q.correct_answers.score) {
                        totalScore += Number(q.correct_answers.score) || 0;
                    }
                }
            });

            // 檢查測驗狀態，如果還沒發布，詢問是否要發布
            try {
                const quizResponse = await getSingleQuiz(id);
                if (quizResponse.result && quizResponse.data) {
                    const quizStatus = quizResponse.data.status;
                    const quizData = quizResponse.data;

                    // 如果還沒發布，顯示確認對話框
                    if (quizStatus !== 'published') {
                        const questionCount = questions.value
                            ? questions.value.length
                            : 0;
                        const passingScore = quizData.passing_score || '-';

                        Modal.confirm({
                            title: '答案設置完成',
                            content: h('div', [
                                h(
                                    'div',
                                    {
                                        style: 'margin-bottom: 12px; font-weight: 600;'
                                    },
                                    ['測驗資訊：']
                                ),
                                h(
                                    'div',
                                    {
                                        style: 'margin-bottom: 8px; padding-left: 16px; line-height: 1.8;'
                                    },
                                    [`總題數：${questionCount} 題`]
                                ),
                                h(
                                    'div',
                                    {
                                        style: 'margin-bottom: 8px; padding-left: 16px; line-height: 1.8;'
                                    },
                                    [`答案設定總分：${totalScore} 分`]
                                ),
                                h(
                                    'div',
                                    {
                                        style: 'margin-bottom: 16px; padding-left: 16px; line-height: 1.8;'
                                    },
                                    [`及格分數：${passingScore} 分`]
                                ),
                                h(
                                    'div',
                                    {
                                        style: 'margin-top: 16px; color: #666; font-size: 14px;'
                                    },
                                    ['是否要立即發布此測驗？']
                                )
                            ]),
                            width: 480,
                            okText: '發布',
                            cancelText: '返回列表',
                            onOk: async () => {
                                try {
                                    await updateQuizzes(id, {
                                        status: 'published'
                                    });
                                    message.success('測驗已發布');
                                    goBack();
                                } catch (error) {
                                    const { response } = error;
                                    const { data: d } = response || {};
                                    const { message: m } = d || {};
                                    showErrorMessage(m || '發布失敗');
                                }
                            },
                            onCancel: () => {
                                goBack();
                            }
                        });
                    }
                }
            } catch (error) {
                console.error('檢查測驗狀態失敗:', error);
                // 即使檢查失敗也不影響，只記錄錯誤
            }
        })
        .catch((err) => {
            const { response } = err;
            const { data: d } = response;
            const { message: m } = d;

            showErrorMessage(m || '設定答案失敗');
        })
        .finally((e) => {
            loading.value = false;
        });
};

onMounted(async () => {
    // 先載入測驗資訊，確保能正確判斷類型
    await loadQuizInfo();
    // 再載入題目（維度類型需要 quizInfo 已載入）
    await loadQuestions();
});
</script>

<style scoped>
.quiz-set-answer-page {
    padding: 24px;
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    min-height: 100vh;
}

.page-header {
    margin-bottom: 32px;
}

.page-header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.page-header h1 {
    font-size: 28px;
    font-weight: 700;
    margin: 0;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    text-shadow: 0 0 10px rgba(42, 74, 122, 0.3);
}

.header-back-button {
    color: rgba(138, 175, 255, 0.8) !important;
    font-family: 'Courier New', monospace !important;
    transition: all 0.2s ease !important;
}

.header-back-button:hover {
    color: #3b6ea5 !important;
    background: rgba(138, 175, 255, 0.1) !important;
}

.content-container {
    max-width: 1400px;
    margin: 0 auto;
}

/* 資訊卡片樣式 */
.info-card {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    border-radius: 16px;
    padding: 24px;
    box-shadow:
        0 8px 24px rgba(42, 74, 122, 0.15),
        0 0 0 1px rgba(42, 74, 122, 0.1);
    backdrop-filter: blur(10px);
}

.quiz-info-card {
    position: sticky;
    top: 24px;
}

.card-title-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 0 0 24px 0;
    padding-bottom: 16px;
    border-bottom: 2px solid rgba(42, 74, 122, 0.3);
}

.card-title {
    font-size: 20px;
    font-weight: 600;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    margin: 0;
}

/* 計分模式 tag 樣式（統一） */
.scoring-mode-tag {
    border: none !important;
    font-family: 'Courier New', monospace !important;
    font-size: 11px !important;
    font-weight: 600 !important;
    padding: 2px 8px !important;
    height: 20px !important;
    line-height: 16px !important;
    border-radius: 4px !important;
    margin-right: 8px !important;
}

.info-content {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.info-item {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.info-label {
    font-size: 12px;
    font-weight: 600;
    color: rgba(42, 42, 58, 0.6);
    font-family: 'Courier New', monospace;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.info-value {
    font-size: 16px;
    font-weight: 500;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
}

.description-value {
    line-height: 1.6;
    white-space: pre-wrap;
    word-break: break-word;
}

/* 右側題目列表 */
.questions-list {
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-bottom: 32px;
}

.question-card {
    background: rgba(255, 255, 255, 0.95);
    border-radius: 16px;
    padding: 24px;
    box-shadow:
        0 2px 12px rgba(150, 150, 160, 0.1),
        0 0 0 1px rgba(150, 150, 160, 0.15);
    backdrop-filter: blur(10px);
    transition: all 0.3s ease;
}

.question-card:hover {
    box-shadow:
        0 4px 16px rgba(140, 150, 160, 0.15),
        0 0 0 1px rgba(140, 150, 160, 0.2);
    transform: translateY(-1px);
}

.question-header {
    margin-bottom: 20px;
    padding-bottom: 16px;
    border-bottom: 1px solid rgba(150, 150, 160, 0.2);
}

.question-labels-row {
    display: grid;
    grid-template-columns: 1fr 1fr 100px;
    gap: 24px;
    margin-bottom: 8px;
    align-items: center;
}

.question-content-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 24px;
}

.question-left-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
}

.question-content-cell.question-type-content {
    flex-shrink: 0;
    display: flex;
    justify-content: flex-end;
    align-items: flex-start;
}

.question-label-cell {
    font-size: 11px;
    font-weight: 600;
    color: rgba(100, 100, 110, 0.6);
    font-family: 'Courier New', monospace;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.question-type-header {
    text-align: right;
}

.question-type-content {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
}

.question-score-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
}

.question-score-label {
    font-size: 14px;
    font-weight: 600;
    color: rgba(42, 42, 58, 0.7);
    font-family: 'Courier New', monospace;
    white-space: nowrap;
}

.question-score-input {
    width: 120px;
    font-family: 'Courier New', monospace !important;
}

:deep(.question-score-input .ant-input-number) {
    width: 120px !important;
}

:deep(.question-score-input .ant-input-number-input) {
    font-size: 14px !important;
    padding: 8px 12px !important;
    height: 40px !important;
}

.question-type-tag {
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
    border-radius: 16px !important;
    padding: 0 8px !important;
    height: auto !important;
    line-height: 1.5 !important;
    font-size: 13px !important;
    border: 1.5px solid !important;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08) !important;
    background: transparent !important;
}

.question-type-tag-single {
    background: #d6e4f0 !important;
    border-color: #a8c5d9 !important;
    color: #5a7a9a !important;
}

.question-type-tag-multiple {
    background: #e8def8 !important;
    border-color: #c7aee8 !important;
    color: #7a5aa0 !important;
}

.question-type-tag-text,
.question-type-tag-long_text {
    background: #e0f5f2 !important;
    border-color: #b8e5dd !important;
    color: #4a8a7a !important;
}

.question-type-tag-number {
    background: #f5e6d6 !important;
    border-color: #e5c5a5 !important;
    color: #9a7a5a !important;
}

.question-type-tag-scale {
    background: #e4f1e5 !important;
    border-color: #c5e5c7 !important;
    color: #5a9a6a !important;
}

.question-type-tag-default {
    background: #e8e8e8 !important;
    border-color: #c0c0c0 !important;
    color: #5a5a5a !important;
}

.question-title-content {
    font-size: 16px;
    font-weight: 600;
    color: rgba(80, 80, 90, 0.85);
    font-family: 'Courier New', monospace;
    line-height: 1.5;
}

.question-description-content {
    font-size: 12px;
    font-weight: 400;
    color: rgba(100, 100, 110, 0.65);
    line-height: 1.4;
    font-family: 'Courier New', monospace;
}

.question-content-placeholder {
    visibility: hidden;
}

.required-star {
    color: rgba(180, 100, 110, 0.8);
    font-weight: bold;
    margin-left: 2px;
}

/* 選項容器 */
.options-container {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.option-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    background: rgba(255, 255, 255, 0.8);
    border: 2px solid rgba(42, 74, 122, 0.2);
    border-radius: 10px;
    transition: all 0.3s ease;
}

.option-row-clickable {
    cursor: pointer;
}

.option-row:hover {
    border-color: rgba(42, 74, 122, 0.4);
    background: rgba(42, 74, 122, 0.05);
}

.option-row.option-selected {
    background: linear-gradient(
        135deg,
        rgba(42, 74, 122, 0.1),
        rgba(138, 175, 255, 0.05)
    );
    border-color: #2a4a7a;
    box-shadow: 0 4px 12px rgba(42, 74, 122, 0.2);
}

.option-left {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
}

.option-radio-wrapper,
.option-checkbox-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    cursor: pointer;
    position: relative;
    flex-shrink: 0;
}

.option-radio-wrapper.option-disabled,
.option-checkbox-wrapper.option-disabled {
    cursor: not-allowed;
    opacity: 0.5;
}

.option-radio-input,
.option-checkbox-input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    width: 0;
    height: 0;
}

.option-radio-mark {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 2px solid rgba(150, 150, 160, 0.4);
    background: #ffffff;
    transition: all 0.3s ease;
    position: relative;
}

.option-radio-input:checked ~ .option-radio-mark {
    background: rgba(160, 170, 180, 0.6);
    border-color: rgba(140, 150, 160, 0.6);
    box-shadow: 0 0 6px rgba(150, 150, 160, 0.3);
}

.option-radio-input:disabled ~ .option-radio-mark,
.option-checkbox-input:disabled ~ .option-checkbox-mark {
    opacity: 0.4;
    cursor: not-allowed;
}

.option-radio-input:disabled:checked ~ .option-radio-mark,
.option-checkbox-input:disabled:checked ~ .option-checkbox-mark {
    background: rgba(160, 170, 180, 0.3);
    border-color: rgba(140, 150, 160, 0.3);
}

.option-radio-input:checked ~ .option-radio-mark::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #ffffff;
}

.option-checkbox-mark {
    width: 20px;
    height: 20px;
    border-radius: 4px;
    border: 2px solid rgba(150, 150, 160, 0.4);
    background: #ffffff;
    transition: all 0.3s ease;
    position: relative;
}

.option-checkbox-input:checked ~ .option-checkbox-mark {
    background: rgba(160, 170, 180, 0.6);
    border-color: rgba(140, 150, 160, 0.6);
    box-shadow: 0 0 6px rgba(150, 150, 160, 0.3);
}

.option-checkbox-input:checked ~ .option-checkbox-mark::after {
    content: '✓';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: #ffffff;
    font-size: 12px;
    font-weight: bold;
    font-family: 'Courier New', monospace;
}

.option-number {
    font-size: 14px;
    font-weight: 600;
    color: rgba(42, 42, 58, 0.7);
    font-family: 'Courier New', monospace;
    min-width: 20px;
}

.option-text {
    font-size: 14px;
    font-weight: 500;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    flex: 1;
}

/* 維度加權區域 */
.option-dimension-weights {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px dashed rgba(42, 74, 122, 0.2);
}

.dimension-weight-item {
    display: flex;
    align-items: center;
    gap: 8px;
}

.dimension-weight-label {
    font-size: 13px;
    font-weight: 600;
    color: rgba(42, 74, 122, 0.9);
    font-family: 'Courier New', monospace;
    min-width: 60px;
}

.dimension-weight-input {
    width: 80px !important;
}

.option-score-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 140px;
    flex-shrink: 0;
}

.score-label-inline {
    font-size: 12px;
    font-weight: 600;
    color: rgba(42, 42, 58, 0.7);
    font-family: 'Courier New', monospace;
    white-space: nowrap;
}

.score-input-inline {
    flex: 1;
    min-width: 120px !important;
    font-family: 'Courier New', monospace !important;
}

:deep(.score-input-inline .ant-input-number) {
    width: 100% !important;
    min-width: 120px !important;
}

/* 文字題答案容器 */
.text-answer-container {
    margin-top: 12px;
}

.text-answer-row {
    display: flex;
    gap: 24px;
    align-items: flex-start;
}

.text-answer-input-wrapper {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.text-answer-score-wrapper {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 200px;
    flex-shrink: 0;
}

.text-answer-label-inline {
    font-size: 12px;
    font-weight: 600;
    color: rgba(42, 42, 58, 0.7);
    font-family: 'Courier New', monospace;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.text-answer-textarea {
    width: 100%;
    font-family: 'Courier New', monospace !important;
    border-radius: 10px !important;
    border-color: rgba(150, 150, 160, 0.3) !important;
}

.text-answer-input {
    width: 100% !important;
    min-width: 300px !important;
    font-family: 'Courier New', monospace !important;
}

.text-answer-textarea:hover {
    border-color: rgba(140, 140, 150, 0.5) !important;
}

.text-answer-textarea:focus {
    border-color: rgba(130, 140, 150, 0.6) !important;
    box-shadow: 0 0 0 2px rgba(150, 150, 160, 0.15) !important;
}

.action-bar {
    margin-top: 40px;
    padding-top: 32px;
    border-top: 1px solid rgba(150, 150, 160, 0.15);
    display: flex;
    justify-content: center;
}

.save-button {
    min-width: 240px;
    height: 52px;
    background: linear-gradient(
        135deg,
        rgba(138, 175, 255, 0.85),
        rgba(42, 74, 122, 0.85)
    ) !important;
    border: none !important;
    color: #ffffff !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 700 !important;
    font-size: 16px !important;
    border-radius: 26px !important;
    box-shadow:
        0 4px 16px rgba(138, 175, 255, 0.3),
        0 2px 8px rgba(42, 74, 122, 0.2) !important;
    transition: all 0.3s ease !important;
    letter-spacing: 0.5px !important;
}

.save-button:hover {
    background: linear-gradient(
        135deg,
        rgba(138, 175, 255, 0.95),
        rgba(42, 74, 122, 0.95)
    ) !important;
    box-shadow:
        0 6px 20px rgba(138, 175, 255, 0.4),
        0 4px 12px rgba(42, 74, 122, 0.3) !important;
    transform: translateY(-2px) !important;
}

.save-button:active {
    transform: translateY(0) !important;
    box-shadow:
        0 2px 12px rgba(138, 175, 255, 0.3),
        0 1px 6px rgba(42, 74, 122, 0.2) !important;
}

.save-button:disabled,
.save-button[disabled] {
    background: rgba(140, 150, 160, 0.5) !important;
    box-shadow: none !important;
    cursor: not-allowed !important;
}

.empty-state {
    text-align: center;
    padding: 120px 24px;
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    border-radius: 16px;
    box-shadow:
        0 8px 24px rgba(42, 74, 122, 0.15),
        0 0 0 1px rgba(42, 74, 122, 0.1);
}

.empty-icon {
    font-size: 64px;
    margin-bottom: 16px;
}

.empty-text {
    font-size: 18px;
    color: rgba(42, 42, 58, 0.6);
    font-family: 'Courier New', monospace;
}

/* Ant Design 元件樣式覆蓋 */
:deep(.ant-input),
:deep(.ant-input-number),
:deep(.ant-input-number-input) {
    border-radius: 10px !important;
    border-color: rgba(150, 150, 160, 0.3) !important;
    font-family: 'Courier New', monospace !important;
    transition: all 0.3s ease !important;
}

:deep(.ant-input:hover),
:deep(.ant-input-number:hover),
:deep(.ant-input-number-input:hover) {
    border-color: rgba(140, 140, 150, 0.5) !important;
    box-shadow: 0 0 0 2px rgba(150, 150, 160, 0.1) !important;
}

:deep(.ant-input:focus),
:deep(.ant-input-focused),
:deep(.ant-input-number-focused),
:deep(.ant-input-number-input:focus) {
    border-color: rgba(130, 140, 150, 0.6) !important;
    box-shadow:
        0 0 0 2px rgba(150, 150, 160, 0.15),
        0 2px 8px rgba(140, 150, 160, 0.1) !important;
}

:deep(.ant-tag) {
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
    border-radius: 6px !important;
    padding: 4px 12px !important;
    font-size: 12px !important;
}
</style>
